// Symbol-local drawings (Slice E1 — SY-06, Q2, spec §2.3).
// TradingView parity: a drawing belongs to its symbol; sync only carries it
// between charts viewing the SAME symbol. Pure helpers, unit-tested.
import { resolvePointAnchor } from "@vninvestcharts/engine-drawing";
/** Stamp the origin symbol on an outgoing op (emit side). */
export function withDrawingSourceSymbol(op, symbol) {
    return symbol ? { ...op, symbol } : op;
}
/**
 * Receive-side gate: apply only same-symbol ops.
 * Legacy ops without a symbol pass through (backward compatible).
 */
export function shouldApplyRemoteDrawing(opSymbol, localSymbol) {
    if (typeof opSymbol !== "string" || opSymbol === "")
        return true;
    return opSymbol === localSymbol;
}
/**
 * P2/D-SYNC-23: resolve a sync timestamp without `Date.now()` garbage. Anchor
 * hints win; indexed plotData second; deterministic 0 when unanchorable
 * (renders off-chart) instead of a fake "now" that collides with live bars.
 */
export function drawingPointToSyncTimestamp(point, plotData) {
    if (typeof point.timeMsHint === "number" && Number.isFinite(point.timeMsHint)) {
        return point.timeMsHint;
    }
    if (typeof point.xIndexFloat === "number" && Number.isFinite(point.xIndexFloat) && plotData) {
        const datum = plotData[Math.round(point.xIndexFloat)];
        const time = datum?.date instanceof Date ? datum.date.getTime() : undefined;
        if (typeof time === "number" && Number.isFinite(time))
            return time;
    }
    return 0;
}
// E2: notify only when the REMOTE op won (the local drawing visibly changed)
// on a slot viewing the same symbol. Legacy conflicts without a symbol notify
// on the active chart only (avoids N-slot noise for unscoped conflicts).
export function shouldNotifyDrawingConflict(info, slotSymbol, isActiveSlot) {
    if (!info || info.winner !== "remote")
        return false;
    const opSymbol = info.remoteOp?.symbol ?? info.localOp?.symbol ?? null;
    if (typeof opSymbol === "string" && opSymbol !== "")
        return opSymbol === slotSymbol;
    return isActiveSlot;
}
// E2-fix: hybrid-logical-clock stamp for outgoing ops. The engine fires its
// conflict callback on every version TIE for a known drawingId, so emitting
// `version: {}` (as done historically) turns each sequential local op
// (drag-update, delete-after-create) into a bogus "remote won" conflict —
// notified even on the chart that drew last. A strictly monotonic
// per-writer timestamp keeps sequential ops comparable (no tie, no notice)
// while true concurrent edits from different writers still tie/diverge and
// notify legitimately. Remount-safe (wall-clock based, no in-memory counter
// that could restart and silently drop ops).
export function nextDrawingOpTimestamp(lastTimestamp, now = Date.now()) {
    if (!Number.isFinite(lastTimestamp))
        return Number.isFinite(now) ? now : 0;
    if (!Number.isFinite(now))
        return lastTimestamp + 1;
    return Math.max(now, lastTimestamp + 1);
}
/**
 * Emit-side point normalization (eraser/brush/ABCD stretch fix).
 * Freehand and multi-step drafts carry bare `{x, y}` points (unix ms + price)
 * until render-path enrichment runs — but sync emit reads the raw workspace
 * points, so `drawingPointToSyncTimestamp(p, ...)` saw no `timeMsHint` and
 * emitted `timestamp: 0` for real points. Receivers then built
 * `{x: 0, timeMsHint: 0}` → `chartPointToPixel` NaN → the clip helper replaced
 * the NaN endpoint with the viewport edge: a full-width stretched line.
 * Normalizing through `resolvePointAnchor` first (which falls back to
 * `p.x > 10000` as the time hint, mirroring the local render path) keeps the
 * wire timestamp real. Genuinely unanchorable points still emit `0`
 * (off-chart by design, P2/D-SYNC-23).
 */
export function drawingPointToSyncFields(point, plotData) {
    const anchor = resolvePointAnchor(point);
    const timestamp = drawingPointToSyncTimestamp({ timeMsHint: anchor.timeMsHint, xIndexFloat: anchor.xIndexFloat }, plotData);
    return {
        timestamp,
        price: anchor.yPrice ?? 0,
        xIndexFloat: anchor.xIndexFloat,
        yPrice: anchor.yPrice,
    };
}
/**
 * Receive-side point mapping (defense for the same stretch bug).
 * A `timestamp: 0` (or missing/non-finite) timestamp must stay absent —
 * fabricating `timeMsHint: 0` creates a NaN projection that the clip helper
 * stretches to the viewport edge. `xIndexFloat` passes through untouched
 * (receiver resolves it in its own index space).
 */
export function syncPointToDrawingPoint(p) {
    const ts = typeof p?.timestamp === "number" && Number.isFinite(p.timestamp) && p.timestamp > 0
        ? p.timestamp
        : undefined;
    return { x: ts, y: p?.price, timeMsHint: ts, yPrice: p?.price, xIndexFloat: p?.xIndexFloat };
}
/**
 * `DrawingObject.style` -> `DrawingStyle`. THE single mapping.
 *
 * `opacity` is included, which two of the three original literals omitted — see the
 * table above. Fixing it here rather than at the three call sites is deliberate: a
 * fix applied to two of three literals is the bug, restated.
 *
 * `zIndex` is deliberately NOT copied into the style. `DrawingData` and `DrawingStyle`
 * both declare it, but the drawings path already carries it as a top-level field, and
 * populating both would create a second source of truth for one value.
 */
export function toSyncStyle(style) {
    if (!style)
        return undefined;
    return {
        color: style.stroke,
        lineWidth: style.strokeWidth,
        lineStyle: style.strokeDasharray,
        fillColor: style.fill,
        opacity: style.opacity,
        fontSize: style.fontSize,
        fontFamily: style.fontFamily,
    };
}
/** `TrendLine` -> `trend-line`. Used by `onDrawingChange` only; ops keep the raw type. */
export function toDrawingDataType(type) {
    return type.replace(/([A-Z])/g, "-$1").toLowerCase().replace(/^-/, "");
}
/** Anchor-normalised points -> `{timestamp, price}`, using the rule above. */
export function toSyncPoints(points, plotData) {
    return points.map((p) => {
        const f = drawingPointToSyncFields(p, plotData);
        return { timestamp: f.timestamp, price: f.price };
    });
}
/**
 * `DrawingObject[]` -> `DrawingData[]` for `onDrawingChange` (workspace persistence).
 *
 * Fallbacks are explicit arguments rather than closure reads. The original read
 * `localSymbol`, `props.timeframe` and `dataResult.plotData` out of the component
 * scope, which is exactly why the literal could not be tested without mounting a
 * 2 900-line component.
 */
export function convertToDrawingData(drawings, scope) {
    return drawings.map((drawing) => ({
        id: drawing.id,
        type: toDrawingDataType(drawing.type),
        symbol: drawing.symbol ?? scope.fallbackSymbol ?? "",
        timeframe: drawing.timeframe ?? scope.fallbackTimeframe ?? "1m",
        points: toSyncPoints(drawing.points, scope.plotData),
        style: toSyncStyle(drawing.style),
        label: drawing.label,
        locked: drawing.locked,
        zIndex: drawing.zIndex,
    }));
}
/**
 * Diff two drawing sets into CRDT operations. Pure: same two arrays in, same
 * operations out.
 *
 * Ordering create -> delete -> update is load-bearing: a delete must never win over a
 * create for the same id, and an update must not be emitted for a drawing that only
 * just appeared in `next` (it has no `prev` to diff against).
 *
 * The per-writer clock is NOT applied here — it lives in the component, which owns the
 * ref, and this function is handed the timestamp to use. A pure diff is testable; a
 * closure that mutates a ref is not, and this rule set is where a real CRDT bug lived.
 */
export function buildDrawingOperations(input) {
    const { prev, next, writerKey, symbol, timeframe, plotData, now, opType } = input;
    const operations = [];
    const version = { [writerKey]: now };
    const base = { version, timestamp: now, authorId: writerKey, symbol };
    const dataOf = (d) => ({
        id: d.id,
        type: opType(d),
        symbol,
        timeframe,
        points: toSyncPoints(d.points, plotData),
        style: toSyncStyle(d.style),
        label: d.label,
        locked: d.locked,
        zIndex: d.zIndex,
    });
    for (const d of next) {
        if (prev.some((p) => p.id === d.id))
            continue;
        operations.push({ id: `op-create-${d.id}-${now}`, drawingId: d.id, type: "create", ...base, data: dataOf(d) });
    }
    for (const d of prev) {
        if (next.some((n) => n.id === d.id))
            continue;
        // Deletes carry no data — the symbol stamp in `base` is the ONLY scope signal a
        // receiver gets for cross-symbol gating and tombstone scope (P2/D-SYNC-24).
        operations.push({ id: `op-delete-${d.id}-${now}`, drawingId: d.id, type: "delete", ...base });
    }
    for (const d of next) {
        const p = prev.find((x) => x.id === d.id);
        if (!p)
            continue;
        const pointsChanged = JSON.stringify(d.points) !== JSON.stringify(p.points);
        const styleChanged = JSON.stringify(d.style) !== JSON.stringify(p.style);
        const labelChanged = d.label !== p.label;
        const lockedChanged = d.locked !== p.locked;
        const zIndexChanged = d.zIndex !== p.zIndex;
        if (!(pointsChanged || styleChanged || labelChanged || lockedChanged || zIndexChanged))
            continue;
        operations.push({
            id: `op-update-${d.id}-${now}`,
            drawingId: d.id,
            // A point move is a distinct op type: the receiver treats it as a drag, not
            // as a full replacement.
            type: pointsChanged ? "move-point" : "update",
            ...base,
            data: dataOf(d),
        });
    }
    return operations;
}
